import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingScreen from '../components/LoadingScreen';
import { MAINTENANCE } from '../config';
import '../design-system.css';

function AuthorLink({ username, label, navigate, style }) {
    return (
        <span
            onClick={(e) => { e.stopPropagation(); navigate(`/creator/${username}`); }}
            style={{ cursor: 'pointer', ...style }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
            {label}
        </span>
    );
}

function BlogPost() {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statementAuthors, setStatementAuthors] = useState([]);

    useEffect(() => {
        if (!post) return;

        const title = post.is_statement
            ? `${post.title} — Official Statement | blog.ie`
            : `${post.title} — ${post.author || ''} | blog.ie`;

        document.title = title;

        return () => {
            document.title = 'winston.ie';
        };
    }, [post]);

    useEffect(() => {
        const fetchPost = async () => {
            const { data } = await supabase.from('posts').select('*').eq('slug', slug).single();
            if (data) {
                setPost(data);
                const allUsernames = [data.author_username, ...(data.co_authors || [])].filter(Boolean);
                if (allUsernames.length > 0) {
                    const { data: matches } = await supabase
                        .from('users')
                        .select('username, first_name, last_name, org_logo_url')
                        .in('username', allUsernames);
                    if (matches) {
                        const ordered = allUsernames.map(u => matches.find(m => m.username === u)).filter(Boolean);
                        setStatementAuthors(ordered);
                    }
                }
            }
        };
        const fetchComments = async () => {
            const { data } = await supabase.from('comments').select('*').eq('post_slug', slug).order('created_at', { ascending: true });
            if (data) setComments(data);
        };
        fetchPost();
        fetchComments();
    }, [slug]);

    const handleComment = async () => {
        if (!name || !content) return;
        setSubmitting(true);
        await supabase.from('comments').insert({ post_slug: slug, name, content, created_at: new Date().toISOString() });
        setComments([...comments, { name, content, created_at: new Date().toISOString() }]);
        setName('');
        setContent('');
        setSubmitting(false);
    };

    const displayLabel = (user, fallback) => {
        if (!user) return fallback;
        return `${user.first_name || ''} ${user.last_name || ''}`.trim() || `@${user.username}`;
    };

    if (!post) return <LoadingScreen variant="light" />;

    if (MAINTENANCE) return (
        <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video autoPlay loop muted playsInline style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: -1 }}>
                <source src="/video/background.mp4" type="video/mp4" />
            </video>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: -1 }} />
            <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>winston.ie</p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: '300', letterSpacing: '0.02em' }}>Quietly reorganizing. Back soon.</p>
            </div>
        </div>
    );

    /* ─────────── STATEMENT LAYOUT ─────────── */
    if (post.is_statement) {
        return (
            <div className="w-page">
                <Navbar variant="light" />
                <div className="w-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '100%', maxWidth: '680px' }}>
                        <p className="w-eyebrow" style={{ cursor: 'pointer' }} onClick={() => navigate('/blog')}>← Blog</p>
                    </div>

                    <div style={{ width: '100%', maxWidth: '680px', background: 'var(--w-bg)', border: '1px solid var(--w-border)', overflow: 'hidden' }}>

                        <div style={{ padding: '32px 40px 26px', borderBottom: '1px solid var(--w-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
                                {statementAuthors.length > 0 ? statementAuthors.map((au, i) => (
                                    <div key={au.username} style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                                        {i > 0 && <span style={{ color: 'var(--w-muted)', fontSize: '13px' }}>×</span>}
                                        <div
                                            onClick={() => navigate(`/creator/${au.username}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {au.org_logo_url ? (
                                                <img
                                                    src={au.org_logo_url}
                                                    alt={displayLabel(au)}
                                                    style={{ maxHeight: '30px', maxWidth: '160px', objectFit: 'contain', display: 'block' }}
                                                    onError={(e) => { console.error('Logo failed to load:', au.org_logo_url); }}
                                                />
                                            ) : (
                                                <p style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--w-ink)' }}>
                                                    {displayLabel(au)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )) : (
                                    <p style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--w-ink)' }}>{post.author}</p>
                                )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <p className="w-eyebrow" style={{ margin: '0 0 6px', fontSize: '9px' }}>
                                    {statementAuthors.length > 1 ? 'Joint Official Statement' : 'Official Statement'}
                                </p>
                                <p style={{ fontSize: '12px', color: 'var(--w-ink-soft)', margin: 0 }}>{new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                {post.location && <p style={{ fontSize: '12px', color: 'var(--w-muted)', margin: '2px 0 0' }}>{post.location}</p>}
                            </div>
                        </div>

                        <div style={{ padding: '40px 40px 32px' }}>
                            <h1 style={{ fontSize: '23px', fontWeight: 700, marginBottom: '26px', lineHeight: 1.35, letterSpacing: '-0.01em', color: 'var(--w-ink)' }}>
                                {post.title}
                            </h1>
                            <div style={{ fontSize: '14.5px', lineHeight: 1.85, color: 'var(--w-ink-soft)', whiteSpace: 'pre-wrap' }}>
                                {post.content}
                            </div>

                            {post.link_url && (
                                <div style={{ marginTop: '32px' }}>
                                    <a
                                        href={post.link_url}
                                        target={post.link_url.startsWith('http') ? '_blank' : '_self'}
                                        rel="noreferrer"
                                        className="wlink"
                                        style={{ display: 'inline-block', textDecoration: 'none', background: 'none', padding: 0, borderRadius: 0, fontWeight: 700, fontSize: '15px', color: 'var(--w-ink)' }}
                                    >
                                        {post.link_url.startsWith('http') ? 'View' : 'See on winston.ie'} →
                                    </a>
                                </div>
                            )}
                        </div>

                        <div style={{ borderTop: '1px solid var(--w-border)', padding: '16px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span style={{ fontSize: '10px', color: 'var(--w-muted)' }}>
                                Issued by {statementAuthors.length > 0 ? statementAuthors.map(a => displayLabel(a)).join(' & ') : post.author}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--w-muted)' }}>Longden Collective · blog.ie / Winston.ie</span>
                        </div>
                    </div>
                </div>
                <Footer variant="light" />
            </div>
        );
    }

    /* ─────────── REGULAR POST LAYOUT ─────────── */
    const regularAuthors = [
        { username: post.author_username, label: statementAuthors.find(a => a.username === post.author_username) ? displayLabel(statementAuthors.find(a => a.username === post.author_username)) : post.author },
        ...(post.co_authors || []).map(u => {
            const match = statementAuthors.find(a => a.username === u);
            return { username: u, label: match ? displayLabel(match) : `@${u}` };
        }),
    ].filter(a => a.username);

    return (
        <div className="w-page">
            <Navbar variant="light" />

            <div className="w-body" style={{ maxWidth: '680px' }}>
                <p className="w-eyebrow" style={{ cursor: 'pointer' }} onClick={() => navigate('/blog')}>← Back to blog</p>

                {post.cover_image && (
                    <div style={{ width: '100%', height: '300px', overflow: 'hidden', marginBottom: '28px' }}>
                        <img src={post.cover_image} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                )}

                <div style={{ fontSize: '11px', color: 'var(--w-muted)', marginBottom: '10px' }}>
                    {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} ·{' '}
                    {regularAuthors.map((a, i) => (
                        <span key={a.username}>
                            {i > 0 && ' & '}
                            <AuthorLink username={a.username} label={a.label} navigate={navigate} style={{ color: 'var(--w-ink-soft)' }} />
                        </span>
                    ))}
                </div>

                <h1 style={{ fontWeight: 700, fontSize: '27px', marginBottom: '24px', lineHeight: 1.35, letterSpacing: '-0.01em', color: 'var(--w-ink)' }}>{post.title}</h1>
                <div style={{ fontSize: '15px', color: 'var(--w-ink-soft)', lineHeight: 1.85, marginBottom: '48px', whiteSpace: 'pre-wrap' }}>{post.content}</div>

                {post.link_url && (
                    <a
                        href={post.link_url}
                        target={post.link_url.startsWith('http') ? '_blank' : '_self'}
                        rel="noreferrer"
                        className="wlink"
                        style={{ display: 'inline-block', textDecoration: 'none', background: 'none', padding: 0, borderRadius: 0, fontWeight: 700, fontSize: '15px', color: 'var(--w-ink)', marginBottom: '24px' }}
                    >
                        {post.link_url.startsWith('http') ? 'View →' : 'See on winston.ie →'}
                    </a>
                )}

                <div style={{ borderTop: '1px solid var(--w-border)', paddingTop: '32px' }}>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '20px', color: 'var(--w-ink)' }}>{comments.length} comment{comments.length !== 1 ? 's' : ''}</h3>

                    {comments.map((comment, index) => (
                        <div key={index} style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <div style={{ width: '26px', height: '26px', background: 'var(--w-bg-soft)', border: '1px solid var(--w-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700 }}>
                                    {comment.name[0].toUpperCase()}
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: 600 }}>{comment.name}</span>
                                <span style={{ fontSize: '11px', color: 'var(--w-muted)' }}>
                                    {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                            </div>
                            <p style={{ fontSize: '13px', color: 'var(--w-ink-soft)', lineHeight: 1.7, paddingLeft: '34px' }}>{comment.content}</p>
                        </div>
                    ))}

                    <div style={{ marginTop: '24px', borderTop: '1px solid var(--w-border)', paddingTop: '24px' }}>
                        <p className="w-field-label">Leave a comment</p>
                        <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Your name" className="w-field-input" style={{ marginBottom: '10px' }}
                        />
                        <textarea
                            value={content} onChange={(e) => setContent(e.target.value)}
                            placeholder="Write a comment..." rows={4} className="w-field-textarea" style={{ marginBottom: '10px' }}
                        />
                        <button
                            onClick={handleComment}
                            disabled={submitting}
                            className="wlink"
                            style={{ background: 'none', border: 'none', padding: 0, borderRadius: 0, fontWeight: 700, fontSize: '14px', color: 'var(--w-ink)', opacity: submitting ? 0.5 : undefined }}
                        >
                            {submitting ? 'Posting...' : 'Post comment →'}
                        </button>
                    </div>
                </div>
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default BlogPost;